import { matchPath } from 'react-router'
import { Dispatch } from 'redux'
import queryString from 'query-string'
import loadable from '@loadable/component'
import { AppRoute } from '@app/common/routes'
import { ROUTES } from '@app/constants'
import { getLocationsCount } from '@app/store/homepage'
import { getCity, getLocations, getState } from '@app/store/locations'
import { getTherapists, getTherapistsByCities } from '@app/store/therapists'
import { getArticle, getCategories, getCategory } from '@app/store/learningStudio'
import { GetCityQuery, GetStateQuery } from '@app/graphql/cms/schema'
import { getJobsList } from '@app/store/careers'

const Homepage = loadable(() => import('@ui/pages/HomePage'))
const ABATherapy = loadable(() => import('@ui/pages/ABATherapy'))
const AutismDiagnosis = loadable(() => import('@ui/pages/AutismDiagnosis'))
const Locations = loadable(() => import('@ui/pages/Locations'))
const SelectedState = loadable(() => import('@ui/pages/SelectedState'))
const SelectedCity = loadable(() => import('@ui/pages/SelectedCity'))
const OurClinicians = loadable(() => import('@ui/pages/OurClinicians'))
const Faq = loadable(() => import('@ui/pages/Faq'))
const LearningStudio = loadable(() => import('@ui/pages/LearningStudio'))
const LearningStudioCategory = loadable(() => import('@ui/pages/LearningStudioCategory'))
const LearningStudioArticle = loadable(() => import('@ui/pages/LearningStudioArticle'))
const CorporateCareers = loadable(() => import('@ui/pages/CorporateCareers'))
const ForPediatricians = loadable(() => import('@ui/pages/ForPediatricians'))
const JoinUs = loadable(() => import('@ui/pages/JoinUs'))
const OurStory = loadable(() => import('@ui/pages/OurStory'))
const ContactUs = loadable(() => import('@ui/pages/ContactUs'))
const Mchat = loadable(() => import('@ui/pages/AutismAssessmentPages/Mchat'))
const ABACostCalculator = loadable(() => import('@ui/pages/ABACostCalculator'))
const Page404 = loadable(() => import('@ui/pages/Page404'))
const TermsAndConditions = loadable(() => import('@ui/pages/AgreementsPages/TermsAndConditions'))
const Policy = loadable(() => import('@ui/pages/AgreementsPages/Policy'))
const OpenRoles = loadable(() => import('@ui/pages/OpenRoles'))
const Booking = loadable(() => import('@ui/pages/Booking'))
const PrivacyPractices = loadable(() => import('@ui/pages/AgreementsPages/PrivacyPractices'))
const NonDiscrimination = loadable(() => import('@ui/pages/AgreementsPages/NonDiscrimination'))
const RbtSignUp = loadable(() => import('@ui/pages/RbtSignUp'))
const Press = loadable(() => import('@ui/pages/Press'))

export const commonRoutes: AppRoute[] = [
  {
    path: ROUTES.homePage,
    exact: true,
    component: Homepage,
    preloadActions: [() => getLocationsCount()]
  },
  {
    path: ROUTES.autismCare,
    exact: true,
    component: ABATherapy
  },
  {
    path: ROUTES.autismDiagnosis,
    exact: true,
    component: AutismDiagnosis
  },
  {
    path: ROUTES.locations,
    exact: true,
    component: Locations,
    preloadActions: [() => getLocations()]
  },
  {
    path: ROUTES.selectedState,
    component: SelectedState,
    preloadActions: [
      ({ url }) => {
        const match = matchPath<{ state?: string }>(url, {
          path: ROUTES.selectedState
        })

        return async (dispatch: Dispatch) => {
          const result = await dispatch(getState(String(match?.params?.state)))
          const citiesIds = (result as GetStateQuery)?.state?.cities?.map(city => city.id) || []
          if (citiesIds.length) {
            await dispatch(getTherapistsByCities(citiesIds))
          }
        }
      }
    ]
  },
  {
    path: ROUTES.selectedCity,
    component: SelectedCity,
    preloadActions: [
      ({ url }) => {
        const match = matchPath<{ city?: string }>(url, {
          path: ROUTES.selectedCity
        })

        return async (dispatch: Dispatch) => {
          const result = await dispatch(getCity(String(match?.params?.city)))
          const selectedCityId = (result as GetCityQuery)?.city?.id
          if (selectedCityId) {
            await dispatch(getTherapistsByCities([selectedCityId]))
          }
        }
      }
    ]
  },
  {
    path: ROUTES.ourClinicians,
    component: OurClinicians,
    preloadActions: [() => getTherapists()]
  },
  {
    path: ROUTES.faq,
    component: Faq
  },
  {
    path: ROUTES.learningStudio,
    component: LearningStudio,
    exact: true,
    preloadActions: [() => getCategories()]
  },
  {
    path: ROUTES.learningStudioCategory,
    component: LearningStudioCategory,
    exact: true,
    preloadActions: [
      ({ url }) => {
        const match = matchPath<{ category?: string }>(url, {
          path: ROUTES.learningStudioCategory
        })

        return async (dispatch: Dispatch) => dispatch(getCategory(String(match?.params?.category)))
      }
    ]
  },
  {
    path: ROUTES.learningStudioArticle,
    component: LearningStudioArticle,
    preloadActions: [
      ({ url }) => {
        interface matchParam {
          article?: string
          category?: string
        }
        const match = matchPath<matchParam>(url, {
          path: ROUTES.learningStudioArticle
        })

        return async (dispatch: Dispatch) => {
          const article = String(match?.params?.article)
          const category = String(match?.params?.category)
          const articleName = queryString.parseUrl(article)?.url
          const articleSlug = decodeURIComponent(`${category}/${articleName}`)
          const result = await dispatch(getArticle(articleSlug))
          return result
        }
      }
    ]
  },
  {
    path: ROUTES.corporateCareers,
    component: CorporateCareers
  },
  {
    path: ROUTES.joinUs,
    component: JoinUs
  },
  {
    path: ROUTES.rbtSignUp,
    component: RbtSignUp
  },
  {
    path: ROUTES.forPediatricians,
    component: ForPediatricians
  },
  {
    path: ROUTES.contactUs,
    component: ContactUs,
    preloadActions: [() => getLocations()]
  },
  {
    path: ROUTES.openRoles,
    component: OpenRoles,
    preloadActions: [() => getJobsList()]
  },
  {
    path: ROUTES.ourStory,
    component: OurStory
  },
  {
    path: ROUTES.mchat,
    component: Mchat
  },
  {
    path: ROUTES.abaCostCalculator,
    component: ABACostCalculator
  },
  {
    path: ROUTES.booking,
    component: Booking
  },
  {
    path: ROUTES.terms,
    component: TermsAndConditions
  },
  {
    path: ROUTES.policy,
    component: Policy
  },
  {
    path: ROUTES.privacyPractices,
    component: PrivacyPractices
  },
  {
    path: ROUTES.nonDiscrimination,
    component: NonDiscrimination
  },
  {
    path: ROUTES.press,
    component: Press
  },
  {
    path: '*',
    component: Page404
  }
]
